import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2GrowthFormSubmission,
  AdminV2GrowthFormSubmissionCrmReviewResponse,
  AdminV2GrowthFormSubmissionExportField,
  AdminV2GrowthFormSubmissionExportResponse,
  AdminV2GrowthFormSubmissionsResponse,
  AdminV2GrowthFormSubmissionStatus,
  AdminV2GrowthOrganisationOptionsResponse,
} from "@/types/adminV2";

export const buildAdminV2GrowthFormSubmissionsEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2GrowthFormOrganisationOptions: builder.query<
    AdminV2GrowthOrganisationOptionsResponse,
    {
      readOwner: string;
      q?: string;
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ readOwner: _readOwner, ...params }) => {
      void _readOwner;
      return {
        url: "/admin/v2/growth/forms/organisation-options",
        params,
        suppressAccessDeniedModal: true,
      };
    },
    keepUnusedDataFor: 0,
  }),

  assignAdminV2GrowthFormSubmissionOrganisation: builder.mutation<
    {
      submission: Pick<
        AdminV2GrowthFormSubmission,
        "id" | "formId" | "organisationId"
      >;
    },
    { formId: string; submissionId: string; organisationId: string }
  >({
    query: ({ formId, submissionId, organisationId }) => ({
      url: `/admin/v2/growth/forms/${formId}/submissions/${submissionId}/organisation`,
      method: "POST",
      data: { organisationId },
    }),
    // The addressed consumer refreshes its current filtered list after confirmation.
  }),

  getAdminV2GrowthFormSubmissions: builder.query<
    AdminV2GrowthFormSubmissionsResponse,
    {
      formId: string;
      /** Cache-only ownership; never forwarded as request params. */
      readOwner?: string;
      q?: string;
      submissionId?: string;
      status?: AdminV2GrowthFormSubmissionStatus;
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ formId, ...params }) => {
      delete params.readOwner;
      return { url: `/admin/v2/growth/forms/${formId}/submissions`, params };
    },
    providesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: `${formId}:submissions` },
    ],
    keepUnusedDataFor: 10,
  }),

  reviewAdminV2GrowthFormSubmissionCrmReview: builder.mutation<
    AdminV2GrowthFormSubmissionCrmReviewResponse,
    {
      formId: string;
      submissionId: string;
      action: "approve" | "queue" | "recheck" | "reject";
      ownerRuntimeActionId: string;
    }
  >({
    query: ({ formId, submissionId, action, ownerRuntimeActionId }) => ({
      url: `/admin/v2/growth/forms/${formId}/submissions/${submissionId}/crm-review`,
      method: "POST",
      data: { action, ownerRuntimeActionId },
    }),
    invalidatesTags: (_result, _error, { formId }) => [
      { type: "AdminV2GrowthForms", id: `${formId}:submissions` },
      { type: "AdminV2GrowthForms", id: formId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  exportAdminV2GrowthFormSubmissions: builder.mutation<
    AdminV2GrowthFormSubmissionExportResponse,
    {
      formId: string;
      body: {
        q?: string;
        selectedFields: AdminV2GrowthFormSubmissionExportField[];
        status?: AdminV2GrowthFormSubmissionStatus;
      };
    }
  >({
    query: ({ formId, body }) => ({
      url: `/admin/v2/growth/forms/${formId}/submissions/export`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: [{ type: "AdminV2AuditLog", id: "LIST" }],
  }),
});
